/**
 * Fase 1I.2 — Painel de Administração Global de Tenants.
 *
 * Acesso protegido no backend por papel GLOBAL ``platform_admin``.
 * O menu já filtra este item pelo AuthContext; esta página é apenas o
 * consumidor final e trata 403 revalidando o acesso silenciosamente.
 *
 * Nunca exibe/loga ``tenant_db_name``, hash, token, senha, URI ou PII
 * do payload — o backend já retorna um contrato restrito.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PauseCircle, PlayCircle, Power, RefreshCw, Building2, Plus,
  Pencil, Archive, ArchiveRestore, Trash2, RotateCcw, AlertTriangle,
  CheckCircle2, XCircle, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const OPAQUE_ERROR = "Não foi possível concluir a operação. Tente novamente.";

// ---------------------------------------------------------------------------
// Utilitários de apresentação
// ---------------------------------------------------------------------------
function fmtDate(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

function fmtRelative(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const s = Math.round(diffMs / 1000);
  if (s < 5) return "agora há pouco";
  if (s < 60) return `há ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `há ${h} h`;
  const dd = Math.round(h / 24);
  if (dd < 30) return `há ${dd} d`;
  return d.toLocaleDateString("pt-BR");
}

const ACTION_LABELS = {
  "tenant.created": "Empresa criada",
  "tenant.updated": "Empresa atualizada",
  "tenant.archived": "Empresa arquivada",
  "tenant.restored": "Empresa restaurada",
  "tenant.deleted": "Empresa excluída",
  "tenant.suspended": "Empresa suspensa",
  "tenant.reactivated": "Empresa reativada",
  "tenant.provisioning_retried": "Provisionamento reexecutado",
  "tenant.owner_invite_rotated": "Convite do responsável renovado",
  "tenant.owner_invite_resent": "Convite do responsável reenviado",
  "recurrences.paused": "Recorrências pausadas",
  "recurrences.resumed": "Recorrências retomadas",
};

const STEP_LABELS = {
  "control_indexes": "Índices do painel global",
  "tenant_indexes": "Índices da empresa",
  "tenant_company": "Configuração da empresa",
  "owner_invite": "Convite do responsável",
};

const STEP_STATUS_LABELS = {
  "done": "Concluída",
  "failed": "Falhou",
  "pending": "Pendente",
};

const ACTION_TONE = {
  "tenant.created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "tenant.updated": "bg-slate-50 text-slate-700 border-slate-200",
  "tenant.archived": "bg-slate-100 text-slate-600 border-slate-200",
  "tenant.restored": "bg-sky-50 text-sky-700 border-sky-200",
  "tenant.deleted": "bg-red-50 text-red-700 border-red-200",
  "tenant.suspended": "bg-amber-50 text-amber-700 border-amber-200",
  "tenant.reactivated": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "tenant.provisioning_retried": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "tenant.owner_invite_rotated": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "tenant.owner_invite_resent": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "recurrences.paused": "bg-amber-50 text-amber-700 border-amber-200",
  "recurrences.resumed": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function StatusPill({ status }) {
  const label = {
    pending: "Provisionando", active: "Ativa",
    suspended: "Suspensa", archived: "Arquivada",
  }[status] || status;
  const cls = {
    pending: "bg-sky-50 text-sky-700 border-sky-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    suspended: "bg-amber-50 text-amber-700 border-amber-200",
    archived: "bg-slate-100 text-slate-500 border-slate-200",
  }[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function ProvPill({ prov }) {
  const label = { ready: "Pronta", pending: "Provisionando", failed: "Falhou" }[prov] || prov;
  const cls = {
    ready: "bg-slate-50 text-slate-700 border-slate-200",
    pending: "bg-sky-50 text-sky-700 border-sky-200",
    failed: "bg-red-50 text-red-700 border-red-200",
  }[prov] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function StepIcon({ status }) {
  if (status === "done") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
  if (status === "failed") return <XCircle className="w-3.5 h-3.5 text-red-600" />;
  return <Clock className="w-3.5 h-3.5 text-slate-400" />;
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
export default function PlatformAdmin() {
  const { platformAdmin, checkPlatformAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseResumeAt, setPauseResumeAt] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // detail dict
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    if (platformAdmin === false) { checkPlatformAdmin(); }
  }, [platformAdmin, checkPlatformAdmin]);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tenants");
      setTenants(Array.isArray(data?.tenants) ? data.tenants : []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error(OPAQUE_ERROR);
    } finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async (tid) => {
    try {
      const params = tid ? { tenant_id: tid, limit: 50 } : { limit: 50 };
      const { data } = await api.get("/admin/audit", { params });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch { setEntries([]); }
  }, []);

  useEffect(() => { loadTenants(); loadAudit(filterTenant); }, [loadTenants, loadAudit, filterTenant]);

  const tenantsByName = useMemo(() => {
    const m = new Map();
    tenants.forEach(t => m.set(t.tenant_id, t.name));
    return m;
  }, [tenants]);

  const groupedEntries = useMemo(() => {
    const groups = new Map();
    entries.forEach(e => {
      const key = e.tenant_id || "—";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    });
    return Array.from(groups.entries());
  }, [entries]);

  const runOp = async (label, fn) => {
    try {
      await fn();
      toast.success(label);
      await loadTenants();
      await loadAudit(filterTenant);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : OPAQUE_ERROR);
      throw err;
    }
  };

  const suspendTenant = async (t) => {
    if (!window.confirm(`Suspender "${t.name}"? Usuários não poderão operar até a reativação.`)) return;
    setBusy(`${t.tenant_id}:suspend`);
    try {
      await runOp("Empresa suspensa.", () =>
        api.patch(`/admin/tenants/${t.tenant_id}/status`, { status: "suspended" }));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const reactivateTenant = async (t) => {
    if (!window.confirm(`Reativar "${t.name}"?`)) return;
    setBusy(`${t.tenant_id}:activate`);
    try {
      await runOp("Empresa reativada.", () =>
        api.patch(`/admin/tenants/${t.tenant_id}/status`, { status: "active" }));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const submitPause = async () => {
    if (!pauseTarget) return;
    const iso = pauseResumeAt ? new Date(pauseResumeAt).toISOString() : "";
    if (!iso) { toast.error("Informe a data/hora futura."); return; }
    setBusy(`${pauseTarget.tenant_id}:pause`);
    try {
      await runOp("Recorrências pausadas.", () =>
        api.patch(`/admin/tenants/${pauseTarget.tenant_id}/recurrences`,
          { action: "pause", resume_at: iso }));
      setPauseTarget(null); setPauseResumeAt("");
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const resumeNow = async (t) => {
    if (!window.confirm(`Retomar recorrências de "${t.name}" agora?`)) return;
    setBusy(`${t.tenant_id}:resume`);
    try {
      await runOp("Recorrências retomadas.", () =>
        api.patch(`/admin/tenants/${t.tenant_id}/recurrences`, { action: "resume" }));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const archiveTenant = async (t) => {
    if (!window.confirm(`Arquivar "${t.name}"? Não será possível editar até restaurar.`)) return;
    setBusy(`${t.tenant_id}:archive`);
    try {
      await runOp("Empresa arquivada.", () =>
        api.post(`/admin/tenants/${t.tenant_id}/archive`));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const restoreTenant = async (t) => {
    if (!window.confirm(`Restaurar "${t.name}" (voltará como suspensa)?`)) return;
    setBusy(`${t.tenant_id}:restore`);
    try {
      await runOp("Empresa restaurada.", () =>
        api.post(`/admin/tenants/${t.tenant_id}/restore`));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const retryProvisioning = async (t) => {
    if (!window.confirm(`Reexecutar provisionamento de "${t.name}"?`)) return;
    setBusy(`${t.tenant_id}:retry`);
    try {
      await runOp("Provisionamento reexecutado.", () =>
        api.post(`/admin/tenants/${t.tenant_id}/retry-provisioning`));
    } catch { /* handled */ } finally { setBusy(null); }
  };

  const openEdit = async (t) => {
    try {
      const { data } = await api.get(`/admin/tenants/${t.tenant_id}`);
      setEditTarget(data);
    } catch {
      toast.error(OPAQUE_ERROR);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="platform-admin-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Administração global
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Gerencie o ciclo de vida das empresas. Este painel não acessa dados operacionais.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="platform-admin-create"
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" /> Nova empresa
          </button>
          <button
            onClick={() => { loadTenants(); loadAudit(filterTenant); }}
            disabled={loading}
            data-testid="platform-admin-refresh"
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </button>
        </div>
      </header>

      {/* Tabela (desktop) */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Empresa</th>
              <th className="text-left px-3 py-2">Slug</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Provisionamento</th>
              <th className="text-right px-3 py-2">Usuários</th>
              <th className="text-left px-3 py-2">Recorrências</th>
              <th className="text-right px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="platform-admin-tenants">
            {tenants.map((t) => (
              <tr key={t.tenant_id}>
                <td className="px-3 py-2">
                  <span className="font-medium text-slate-900">{t.name}</span>
                  {t.current && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] rounded bg-slate-900 text-white" data-testid={`current-badge-${t.slug}`}>
                      Empresa atual
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500">{t.slug}</td>
                <td className="px-3 py-2"><StatusPill status={t.status} /></td>
                <td className="px-3 py-2"><ProvPill prov={t.provisioning_status} /></td>
                <td className="px-3 py-2 text-right tabular-nums">{t.active_memberships}</td>
                <td className="px-3 py-2 text-slate-600">
                  {t.recurrence_paused
                    ? <>Pausadas até {fmtDate(t.recurrence_resume_at)}</>
                    : t.status === "active" ? "Ativas" : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    <button
                      onClick={() => openEdit(t)}
                      disabled={busy || t.status === "archived"}
                      title="Editar"
                      data-testid={`edit-${t.slug}`}
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Pencil className="w-3 h-3 inline mr-1" /> Editar
                    </button>
                    {t.provisioning_status === "failed" && (
                      <button
                        onClick={() => retryProvisioning(t)}
                        disabled={busy}
                        data-testid={`retry-${t.slug}`}
                        className="text-xs px-2 py-1 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3 inline mr-1" /> Retry
                      </button>
                    )}
                    {t.status === "active" && (
                      <button
                        onClick={() => suspendTenant(t)}
                        disabled={t.current || busy}
                        title={t.current ? "Troque de empresa antes de suspender esta" : "Suspender"}
                        data-testid={`suspend-${t.slug}`}
                        className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                      >
                        <Power className="w-3 h-3 inline mr-1" /> Suspender
                      </button>
                    )}
                    {t.status === "suspended" && (
                      <button
                        onClick={() => reactivateTenant(t)}
                        disabled={busy}
                        data-testid={`activate-${t.slug}`}
                        className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        <Power className="w-3 h-3 inline mr-1" /> Reativar
                      </button>
                    )}
                    {t.status === "active" && (
                      <>
                        <button
                          onClick={() => { setPauseTarget(t); setPauseResumeAt(""); }}
                          disabled={busy}
                          data-testid={`pause-${t.slug}`}
                          className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                        >
                          <PauseCircle className="w-3 h-3 inline mr-1" /> Pausar
                        </button>
                        <button
                          onClick={() => resumeNow(t)}
                          disabled={busy || !t.recurrence_paused}
                          data-testid={`resume-${t.slug}`}
                          className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                        >
                          <PlayCircle className="w-3 h-3 inline mr-1" /> Retomar
                        </button>
                      </>
                    )}
                    {t.status !== "archived" && (
                      <button
                        onClick={() => archiveTenant(t)}
                        disabled={t.current || busy}
                        data-testid={`archive-${t.slug}`}
                        className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <Archive className="w-3 h-3 inline mr-1" /> Arquivar
                      </button>
                    )}
                    {t.status === "archived" && (
                      <button
                        onClick={() => restoreTenant(t)}
                        disabled={busy}
                        data-testid={`restore-${t.slug}`}
                        className="text-xs px-2 py-1 rounded border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                      >
                        <ArchiveRestore className="w-3 h-3 inline mr-1" /> Restaurar
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (!t.can_delete || t.current || busy) return;
                        setDeleteTarget(t); setDeleteConfirm("");
                      }}
                      disabled={t.current || busy || !t.can_delete}
                      title={!t.can_delete
                        ? "Empresa em uso. Arquive-a em vez de excluir."
                        : "Excluir"}
                      data-testid={`delete-${t.slug}`}
                      className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {tenants.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Nenhuma empresa.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cards (mobile) */}
      <div className="md:hidden space-y-3">
        {tenants.map((t) => (
          <div key={t.tenant_id} className="bg-white border border-slate-200 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.slug}</div>
              </div>
              {t.current && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-white">Atual</span>}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
              <StatusPill status={t.status} />
              <ProvPill prov={t.provisioning_status} />
              <span className="text-slate-500 ml-auto">{t.active_memberships} usuários</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              <button onClick={() => openEdit(t)} disabled={busy || t.status === "archived"}
                className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Editar</button>
              {t.provisioning_status === "failed" && (
                <button onClick={() => retryProvisioning(t)} disabled={busy}
                  className="text-xs px-2 py-1 rounded border border-indigo-200 text-indigo-700 disabled:opacity-50">Retry</button>
              )}
              {t.status === "active" && (
                <button onClick={() => suspendTenant(t)} disabled={t.current || busy}
                  className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-700 disabled:opacity-50">Suspender</button>
              )}
              {t.status === "suspended" && (
                <button onClick={() => reactivateTenant(t)} disabled={busy}
                  className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 disabled:opacity-50">Reativar</button>
              )}
              {t.status === "active" && (
                <>
                  <button onClick={() => { setPauseTarget(t); setPauseResumeAt(""); }} disabled={busy}
                    className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Pausar</button>
                  <button onClick={() => resumeNow(t)} disabled={busy || !t.recurrence_paused}
                    className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Retomar</button>
                </>
              )}
              {t.status !== "archived" ? (
                <button onClick={() => archiveTenant(t)} disabled={t.current || busy}
                  className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Arquivar</button>
              ) : (
                <button onClick={() => restoreTenant(t)} disabled={busy}
                  className="text-xs px-2 py-1 rounded border border-sky-200 text-sky-700 disabled:opacity-50">Restaurar</button>
              )}
              <button onClick={() => {
                if (!t.can_delete || t.current || busy) return;
                setDeleteTarget(t); setDeleteConfirm("");
              }}
                disabled={t.current || busy || !t.can_delete}
                title={!t.can_delete ? "Empresa em uso. Arquive-a em vez de excluir." : "Excluir"}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 disabled:opacity-50 disabled:cursor-not-allowed">Excluir</button>
            </div>
          </div>
        ))}
      </div>

      {/* Modais */}
      {pauseTarget && (
        <PauseModal
          target={pauseTarget}
          value={pauseResumeAt}
          onChange={setPauseResumeAt}
          onCancel={() => setPauseTarget(null)}
          onSubmit={submitPause}
          busy={busy}
        />
      )}
      {createOpen && (
        <CreateTenantModal
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await loadTenants();
            await loadAudit(filterTenant);
          }}
        />
      )}
      {editTarget && (
        <EditTenantModal
          detail={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await loadTenants();
            await loadAudit(filterTenant);
          }}
        />
      )}
      {deleteTarget && (
        <DeleteTenantModal
          target={deleteTarget}
          value={deleteConfirm}
          onChange={setDeleteConfirm}
          onCancel={() => { setDeleteTarget(null); setDeleteConfirm(""); }}
          onConfirmed={async () => {
            setDeleteTarget(null); setDeleteConfirm("");
            await loadTenants();
            await loadAudit(filterTenant);
          }}
        />
      )}

      {/* Auditoria */}
      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-900">Histórico global</h2>
          <select value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}
            data-testid="audit-filter-tenant"
            className="text-sm border border-slate-200 rounded-lg px-2 py-1">
            <option value="">Todas as empresas</option>
            {tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-4" data-testid="audit-entries">
          {groupedEntries.map(([tid, list]) => (
            <div key={tid} className="border border-slate-100 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">
                {list[0]?.tenant_name || "Empresa removida"}
              </div>
              <ul className="space-y-1.5 text-sm">
                {list.map((e, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border ${ACTION_TONE[e.action] || "bg-slate-50 text-slate-600 border-slate-200"}`}
                      >
                        {ACTION_LABELS[e.action] || e.action}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap" title={fmtDate(e.created_at)}>
                      {fmtRelative(e.created_at)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {groupedEntries.length === 0 && (
            <div className="text-slate-400 text-center py-4">Sem eventos.</div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modais
// ---------------------------------------------------------------------------
function PauseModal({ target, value, onChange, onCancel, onSubmit, busy }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="pause-modal">
      <div className="bg-white rounded-xl w-full max-w-sm p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Pausar recorrências</h3>
        <p className="text-sm text-slate-600">
          Empresa <b>{target.name}</b>. Escolha a data/hora futura de retomada.
        </p>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid="pause-resume-at"
          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200">
            Cancelar
          </button>
          <button onClick={onSubmit} disabled={!value || busy}
            data-testid="pause-submit"
            className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-50">
            Pausar
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTenantModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = name.trim() && slug.trim() && ownerEmail.trim() && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/tenants", {
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        owner_email: ownerEmail.trim(),
        description: description.trim() || null,
      });
      toast.success("Empresa criada. Provisionamento iniciado.");
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : OPAQUE_ERROR);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="create-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Nova empresa</h3>
        <div className="space-y-2 text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Nome</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="create-name"
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Slug (minúsculas, dígitos e hífen)</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)}
              data-testid="create-slug"
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">E-mail do responsável</label>
            <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
              data-testid="create-owner-email"
              type="email"
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Descrição (opcional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="create-description" rows={2} maxLength={500}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200">
            Cancelar
          </button>
          <button onClick={submit} disabled={!canSubmit}
            data-testid="create-submit"
            className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-50">
            Criar
          </button>
        </div>
      </div>
    </div>
  );
}

function EditTenantModal({ detail, onClose, onSaved }) {
  const [name, setName] = useState(detail.name || "");
  const [slug, setSlug] = useState(detail.slug || "");
  const [description, setDescription] = useState(detail.description || "");
  const [ownerEmail, setOwnerEmail] = useState(detail.owner_email || "");
  const [saving, setSaving] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(null);
  const [expiresAt, setExpiresAt] = useState(detail.owner_invite_expires_at || null);
  const ownerLocked = !!detail.owner_invite_accepted;

  const submit = async () => {
    const body = {};
    if (name.trim() && name.trim() !== detail.name) body.display_name = name.trim();
    if (slug.trim() && slug.trim() !== detail.slug) body.slug = slug.trim().toLowerCase();
    if ((description || "").trim() !== (detail.description || "")) {
      if ((description || "").trim() === "") body.clear_description = true;
      else body.description = description.trim();
    }
    if (!ownerLocked && ownerEmail.trim() && ownerEmail.trim() !== detail.owner_email) {
      body.owner_email = ownerEmail.trim();
    }
    if (Object.keys(body).length === 0) {
      toast.info("Nada a alterar.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/tenants/${detail.tenant_id}`, body);
      toast.success("Empresa atualizada.");
      if (data?.accept_url) {
        try {
          await navigator.clipboard.writeText(data.accept_url);
          toast.success("Novo link do convite copiado.");
        } catch {
          /* clipboard denied */
        }
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : OPAQUE_ERROR);
    } finally { setSaving(false); }
  };

  const copyInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy("copy");
    try {
      const { data } = await api.post(`/admin/tenants/${detail.tenant_id}/owner-invite-link`);
      if (data?.accept_url) {
        await navigator.clipboard.writeText(data.accept_url);
        toast.success("Link do convite copiado.");
        if (data.expires_at) setExpiresAt(data.expires_at);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : OPAQUE_ERROR);
    } finally { setInviteBusy(null); }
  };

  const resendInvite = async () => {
    if (inviteBusy) return;
    setInviteBusy("resend");
    try {
      const { data } = await api.post(`/admin/tenants/${detail.tenant_id}/resend-owner-invite`);
      if (data?.email_sent) {
        toast.success("Convite reenviado por e-mail.");
      } else {
        toast.info("Convite rotacionado. Provedor de e-mail indisponível — copie o link se preciso.");
      }
      if (data?.expires_at) setExpiresAt(data.expires_at);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : OPAQUE_ERROR);
    } finally { setInviteBusy(null); }
  };

  const steps = detail.provisioning_steps || {};

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="edit-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Editar empresa</h3>
        <div className="space-y-2 text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Nome</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="edit-name"
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)}
              data-testid="edit-slug"
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">
              E-mail do responsável {ownerLocked && <span className="text-slate-400">(bloqueado — convite já aceito)</span>}
            </label>
            <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
              data-testid="edit-owner-email"
              type="email" disabled={ownerLocked}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-0.5">Descrição</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="edit-description" rows={2} maxLength={500}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5" />
          </div>
        </div>
        <div className="border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold text-slate-500 mb-1">Convite do responsável</div>
          {ownerLocked ? (
            <div className="text-xs text-slate-500">Convite já aceito — não é possível rotacionar.</div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={copyInvite} disabled={!!inviteBusy}
                  data-testid="edit-copy-invite"
                  className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                >Copiar convite</button>
                <button
                  onClick={resendInvite} disabled={!!inviteBusy}
                  data-testid="edit-resend-invite"
                  className="text-xs px-2 py-1 rounded border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                >Reenviar convite</button>
              </div>
              {expiresAt && (
                <div className="text-xs text-slate-500">
                  Convite válido até <span className="font-mono">{fmtDate(expiresAt)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold text-slate-500 mb-1">Etapas de provisionamento</div>
          <ul className="text-xs space-y-1">
            {Object.entries(steps).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-slate-700">
                  <StepIcon status={v.status} /> {STEP_LABELS[k] || k}
                </span>
                <span className="text-slate-400">
                  {v.status === "failed" && v.error_code
                    ? v.error_code
                    : (STEP_STATUS_LABELS[v.status] || v.status)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving}
            data-testid="edit-submit"
            className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-50">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteTenantModal({ target, value, onChange, onCancel, onConfirmed }) {
  const [saving, setSaving] = useState(false);
  const match = value.trim() === target.slug;
  const submit = async () => {
    if (!match) return;
    setSaving(true);
    try {
      await api.delete(`/admin/tenants/${target.tenant_id}`, {
        data: { confirmation_slug: value.trim() },
      });
      toast.success("Empresa excluída.");
      onConfirmed();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : OPAQUE_ERROR);
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="delete-modal">
      <div className="bg-white rounded-xl w-full max-w-sm p-4 space-y-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" /> Excluir empresa
        </h3>
        <p className="text-sm text-slate-600">
          Esta ação é <b>irreversível</b>. O banco operacional da empresa será
          apagado imediatamente. Para confirmar, digite o slug{" "}
          <span className="font-mono text-xs px-1 py-0.5 rounded bg-slate-100">{target.slug}</span>:
        </p>
        <input value={value} onChange={(e) => onChange(e.target.value)}
          data-testid="delete-confirm"
          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200">
            Cancelar
          </button>
          <button onClick={submit} disabled={!match || saving}
            data-testid="delete-submit"
            className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-50">
            Excluir definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}
